import React, { useState, useRef, useEffect } from 'react';
import { MicrophoneIcon, StopIcon, CheckCircleIcon, PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

const DEFAULT_QUESTIONS = [
  { id: 1, text: "Tell me about a happy childhood memory", selected: true, maxDuration: 6, minDuration: 6 },
  { id: 2, text: "Describe someone who influenced your life", selected: true, maxDuration: 6 },
  { id: 3, text: "What's a challenge you overcame?", selected: true, maxDuration: 6 },
  { id: 4, text: "Share advice you'd give your younger self", selected: true, maxDuration: 6 },
  { id: 5, text: "What are your core values?", selected: true, maxDuration: 6 },
  { id: 6, text: "Tell me about your family", selected: true, maxDuration: 6 },
  { id: 7, text: "What's your proudest achievement?", selected: true, maxDuration: 6 },
  { id: 8, text: "What lessons do you want to pass on?", selected: true, maxDuration: 6 },
  { id: 9, text: "Describe a turning point in your life", selected: true, maxDuration: 6 },
  { id: 10, text: "What brings you joy?", selected: true, maxDuration: 6 },
  { id: 11, text: "What do you hope people remember about you?", selected: true, maxDuration: 6 }
];

export default function PodcastInterview() {
  const { user, isAuthenticated } = useAuth();
  const [customizing, setCustomizing] = useState(true);
  const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);
  const [newQuestion, setNewQuestion] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [timer, setTimer] = useState(0);
  const [currentTranscription, setCurrentTranscription] = useState('');
  const [currentInsight, setCurrentInsight] = useState('');
  const [previewVoiceId, setPreviewVoiceId] = useState(null);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  const [showingPreview, setShowingPreview] = useState(false);
  const [audioSegments, setAudioSegments] = useState([]);
  const [compilingPodcast, setCompilingPodcast] = useState(false);
  const [finalPodcastUrl, setFinalPodcastUrl] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  const selectedQuestions = questions.filter(q => q.selected);

  // Load saved progress on mount
  useEffect(() => {
    if (user?.id) {
      loadSavedProgress();
    }
  }, [user]);

  const loadSavedProgress = async () => {
    try {
      const response = await axios.get(`${API_URL}/podcast/progress/${user.id}`);
      if (response.data.session) {
        setSessionId(response.data.session.id);
        setCurrentQuestion(response.data.session.current_question || 0);
        setPreviewVoiceId(response.data.session.preview_voice_id);
        setPreviewAudioUrl(response.data.session.preview_audio_url);
        
        // Load saved recordings
        if (response.data.session.recordings && response.data.session.recordings.length > 0) {
          const loadedChunks = response.data.session.recordings.map(rec => ({
            question: rec.question_text,
            audio: null, // Audio blob not available, but path is saved
            audioPath: rec.audio_path,
            duration: rec.duration,
            insight: 'Previously recorded'
          }));
          setRecordedChunks(loadedChunks);
        }
        
        if (response.data.session.current_question > 0) {
          setCustomizing(false);
        }
      }
    } catch (error) {
      console.log('No saved progress found');
    }
  };

  const saveProgress = async (nextQuestion) => {
    try {
      const formData = new FormData();
      formData.append('user_id', user.id);
      if (sessionId) formData.append('session_id', sessionId);
      formData.append('current_question', nextQuestion);
      if (previewVoiceId) formData.append('preview_voice_id', previewVoiceId);
      if (previewAudioUrl) formData.append('preview_audio_url', previewAudioUrl);

      const response = await axios.post(`${API_URL}/podcast/save-progress`, formData);
      if (!sessionId) {
        setSessionId(response.data.session_id);
      }
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  const toggleQuestion = (id) => {
    setQuestions(questions.map(q => 
      q.id === id ? { ...q, selected: !q.selected } : q
    ));
  };

  const addCustomQuestion = () => {
    if (newQuestion.trim()) {
      const newId = Math.max(...questions.map(q => q.id), 0) + 1;
      setQuestions([...questions, { 
        id: newId, 
        text: newQuestion.trim(), 
        selected: true,
        custom: true,
        maxDuration: 90  // Default 90 seconds for custom questions
      }]);
      setNewQuestion('');
    }
  };

  const startEdit = (q) => {
    setEditingId(q.id);
    setEditText(q.text);
  };

  const saveEdit = () => {
    if (editText.trim()) {
      setQuestions(questions.map(q => 
        q.id === editingId ? { ...q, text: editText.trim() } : q
      ));
    }
    setEditingId(null);
    setEditText('');
  };

  const deleteQuestion = (id) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const startPodcast = () => {
    if (selectedQuestions.length === 0) {
      alert('Please select at least one question');
      return;
    }
    setCustomizing(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        handleAnswerComplete(blob);
        stream.getTracks().forEach(track => track.stop());
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setTimer(0);

      const maxDuration = selectedQuestions[currentQuestion].maxDuration;
      
      timerIntervalRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev >= maxDuration - 1) {
            stopRecording();
            return maxDuration;
          }
          return prev + 1;
        });
      }, 1000);
      
      // Auto-stop at max duration
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, maxDuration * 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Check minimum duration for first question
      const currentQ = selectedQuestions[currentQuestion];
      if (currentQuestion === 0 && currentQ.minDuration && timer < currentQ.minDuration) {
        alert(`Please record for at least ${currentQ.minDuration} seconds for voice cloning. Current: ${timer}s`);
        return;
      }
      
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const generateHostQuestion = async (questionNumber, questionText) => {
    try {
      const response = await axios.post(`${API_URL}/podcast/generate-host-question`, {
        question_text: questionText,
        question_number: questionNumber
      });
      
      return response.data;
    } catch (error) {
      console.error('Failed to generate host question:', error);
      return null;
    }
  };

  const compileFinalPodcast = async (chunks, segments) => {
    setCompilingPodcast(true);
    
    try {
      console.log('Compiling podcast with segments:', segments);
      console.log('Chunks:', chunks);
      
      // Upload all answer audio blobs to get server paths
      const uploadedSegments = [];
      
      // Use chunks if segments is empty (fallback)
      const audioChunks = segments.length > 0 ? segments : chunks;
      
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        
        let audioPath;
        
        // If audio blob exists, upload it
        if (chunk.audio) {
          const formData = new FormData();
          formData.append('file', chunk.audio, `answer_q${i + 1}.webm`);
          
          console.log(`Uploading answer ${i + 1}...`);
          const uploadResponse = await axios.post(`${API_URL}/upload-audio`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          console.log(`Answer ${i + 1} uploaded:`, uploadResponse.data.file_path);
          audioPath = uploadResponse.data.file_path;
        } else if (chunk.audioPath) {
          // Use existing path from database
          console.log(`Using saved audio path for answer ${i + 1}:`, chunk.audioPath);
          audioPath = chunk.audioPath;
        } else {
          console.error(`No audio available for answer ${i + 1}`);
          continue;
        }
        
        uploadedSegments.push({
          type: 'answer',
          path: audioPath,
          question_text: chunk.question,
          question_number: i + 1
        });
      }
      
      console.log('Uploaded segments:', uploadedSegments);
      
      // Compile podcast on backend
      const compileResponse = await axios.post(`${API_URL}/podcast/compile`, {
        user_id: user?.id || 'user123',
        session_id: sessionId,
        audio_segments: uploadedSegments
      });
      
      setFinalPodcastUrl(compileResponse.data.podcast_url);
      setCompleted(true);
      setResult({
        message: 'Your podcast is ready!',
        podcast_url: compileResponse.data.podcast_url,
        duration: Math.round(compileResponse.data.duration_seconds)
      });
      
    } catch (error) {
      console.error('Failed to compile podcast:', error);
      console.error('Error details:', error.response?.data);
      alert('Failed to compile podcast: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCompilingPodcast(false);
    }
  };

  const handleAnswerComplete = async (audioBlob) => {
    setProcessing(true);
    
    try {
      // Generate host question audio for NEXT question (before processing current answer)
      let hostQuestionData = null;
      if (currentQuestion < selectedQuestions.length - 1) {
        const nextQuestion = selectedQuestions[currentQuestion + 1];
        hostQuestionData = await generateHostQuestion(currentQuestion + 2, nextQuestion.text);
      }
      
      const formData = new FormData();
      formData.append('audio', audioBlob, 'answer.webm');
      
      const response = await axios.post(
        `${API_URL}/transcribe-quick`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const insight = response.data.insight || 'Processing your story...';
      
      setCurrentInsight(insight);
      
      // Store audio segments for final podcast compilation
      const newSegments = [...audioSegments];
      
      // Add host question audio (if this is not the first question)
      if (currentQuestion > 0 && audioSegments.length > 0) {
        // Host question was generated in previous iteration
      }
      
      // Add user answer audio
      const answerUrl = URL.createObjectURL(audioBlob);
      newSegments.push({
        type: 'answer',
        audio: audioBlob,
        url: answerUrl,
        question: selectedQuestions[currentQuestion].text,
        questionNumber: currentQuestion + 1
      });
      
      // Add next host question if available
      if (hostQuestionData) {
        newSegments.push({
          type: 'host',
          path: hostQuestionData.audio_path,
          url: hostQuestionData.audio_url,
          questionNumber: hostQuestionData.question_number
        });
      }
      
      setAudioSegments(newSegments);
      
      const newChunks = [...recordedChunks, {
        question: selectedQuestions[currentQuestion].text,
        audio: audioBlob,
        duration: timer,
        insight: insight
      }];
      setRecordedChunks(newChunks);
      
      // Upload and save recording to database
      const uploadFormData = new FormData();
      uploadFormData.append('file', audioBlob, `q${currentQuestion + 1}.webm`);
      const uploadResp = await axios.post(`${API_URL}/upload-audio`, uploadFormData);
      
      await axios.post(`${API_URL}/podcast/save-recording`, {
        user_id: user?.id || 'user123',
        session_id: sessionId,
        question_number: currentQuestion + 1,
        question_text: selectedQuestions[currentQuestion].text,
        audio_path: uploadResp.data.file_path,
        duration: timer,
        transcription: currentTranscription
      });
      
      // After FIRST question: Clone voice and generate preview
      if (currentQuestion === 0 && !previewVoiceId) {
        await generateVoicePreview(audioBlob);
      }
      
      // Save progress to database
      await saveProgress(currentQuestion + 1);
      
      // Show for 5 seconds so user can read
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      setCurrentTranscription('');
      setCurrentInsight('');
      
      if (currentQuestion < selectedQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // All questions complete - compile final podcast
        await compileFinalPodcast(newChunks, newSegments);
      }
      
    } catch (error) {
      console.error('Failed to transcribe:', error);
      
      const newChunks = [...recordedChunks, {
        question: selectedQuestions[currentQuestion].text,
        audio: audioBlob,
        duration: timer,
        insight: 'Unable to generate insight'
      }];
      setRecordedChunks(newChunks);
      
      if (currentQuestion < selectedQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        processPodcast(newChunks);
      }
    } finally {
      setProcessing(false);
    }
  };

  const generateVoicePreview = async (audioBlob) => {
    try {
      setShowingPreview(true);
      
      // Upload and clone voice from first answer
      const formData = new FormData();
      formData.append('file', audioBlob, 'first_answer.webm');
      
      const uploadResponse = await axios.post(
        `${API_URL}/upload-audio`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const cloneResponse = await axios.post(`${API_URL}/voice/clone`, {
        user_id: user?.id || 'demo_user',
        audio_url: uploadResponse.data.file_path,
        voice_name: `${user?.name || 'User'}'s Voice (Preview)`,
        description: 'Preview voice clone from first answer'
      });
      
      const voiceId = cloneResponse.data.voice_id;
      setPreviewVoiceId(voiceId);
      
      // Save voice_id to database immediately
      try {
        const saveVoiceResp = await axios.post(`${API_URL}/podcast/save-voice`, {
          user_id: user?.id || 'user123',
          voice_id: voiceId,
          session_id: sessionId
        });
        
        // Update session_id if it was created
        if (saveVoiceResp.data.session_id && !sessionId) {
          setSessionId(saveVoiceResp.data.session_id);
        }
      } catch (error) {
        console.error('Failed to save voice_id:', error);
      }
      
      // Generate preview message
      const previewText = "Happy birthday! I'm so proud of you and everything you've accomplished. I love you.";
      
      const generateResponse = await axios.post(`${API_URL}/voice/generate`, {
        text: previewText,
        voice_id: voiceId,
        user_id: user?.id || 'demo_user'
      });
      
      setPreviewAudioUrl(generateResponse.data.audio_url);
      
    } catch (error) {
      console.error('Failed to generate preview:', error);
    } finally {
      setShowingPreview(false);
    }
  };

  const processPodcast = async (chunks) => {
    setProcessing(true);

    try {
      const formData = new FormData();
      chunks.forEach((chunk, i) => {
        formData.append(`chunk_${i}`, chunk.audio, `chunk_${i}.webm`);
      });
      formData.append('user_id', user?.id || 'demo_user');

      const response = await axios.post(
        `${API_URL}/podcast/process`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setResult(response.data);
      setCompleted(true);

    } catch (error) {
      console.error('Failed to process podcast:', error);
      alert('Failed to process podcast: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Login required
  if (!isAuthenticated) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Create Your AI Personality</h1>
          <p className="text-gray-600 mb-8">
            Sign in to start recording your podcast and create your voice clone.
          </p>
          <a
            href="/login"
            className="inline-block px-8 py-4 bg-primary-600 text-white text-lg font-semibold rounded-lg hover:bg-primary-700"
          >
            Sign In to Continue
          </a>
        </div>
      </div>
    );
  }

  // Customization Screen
  if (customizing) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Customize Your Podcast</h1>
          <p className="text-gray-600 mb-8">
            Select questions you want to answer, edit them, or add your own. Your progress will be saved.
          </p>

          {/* Question List */}
          <div className="space-y-3 mb-8">
            {questions.map((q) => (
              <div key={q.id} className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={q.selected}
                  onChange={() => toggleQuestion(q.id)}
                  className="mt-1 h-5 w-5 text-primary-600 rounded"
                />
                
                {editingId === q.id ? (
                  <div className="flex-1 flex space-x-2">
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg"
                      autoFocus
                    />
                    <button
                      onClick={saveEdit}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="flex-1 text-gray-900">{q.text}</p>
                    <button
                      onClick={() => startEdit(q)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {q.custom && (
                      <button
                        onClick={() => deleteQuestion(q.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Add Custom Question */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add Your Own Question
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomQuestion()}
                placeholder="Type your question here..."
                className="flex-1 px-4 py-3 border rounded-lg"
              />
              <button
                onClick={addCustomQuestion}
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add
              </button>
            </div>
          </div>

          {/* Start Button */}
          <div className="flex justify-between items-center pt-6 border-t">
            <p className="text-gray-600">
              {selectedQuestions.length} question{selectedQuestions.length !== 1 ? 's' : ''} selected
            </p>
            <button
              onClick={startPodcast}
              disabled={selectedQuestions.length === 0}
              className="px-8 py-4 bg-primary-600 text-white text-lg font-semibold rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
            >
              Begin Recording →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Rest of the podcast flow (completed, processing, recording)
  if (completed && result) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-gradient-to-br from-purple-900 via-black to-pink-900 min-h-screen p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-500/20 mb-6 backdrop-blur-sm border border-green-500/30">
            <CheckCircleIcon className="h-12 w-12 text-green-400" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">🎙️ Your Podcast is Ready!</h2>
          <p className="text-xl text-purple-200 mb-8">
            {result.message || 'Your AI-hosted podcast has been compiled and is ready to share.'}
          </p>

          {finalPodcastUrl && (
            <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-8 mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">🎧 Listen to Your Podcast</h3>
              <audio 
                controls 
                src={`${API_URL}${finalPodcastUrl}`}
                className="w-full mb-6"
                style={{filter: 'invert(1) hue-rotate(180deg)'}}
              />
              
              <div className="grid grid-cols-2 gap-4 text-left mb-6">
                <div className="bg-purple-900/30 p-4 rounded-lg border border-purple-500/20">
                  <p className="text-sm text-purple-300">Duration</p>
                  <p className="text-2xl font-bold text-white">{result.duration} sec</p>
                </div>
                <div className="bg-pink-900/30 p-4 rounded-lg border border-pink-500/20">
                  <p className="text-sm text-pink-300">Questions</p>
                  <p className="text-2xl font-bold text-white">{selectedQuestions.length}</p>
                </div>
              </div>

              <a
                href={`${API_URL}${finalPodcastUrl}`}
                download="my-legacy-podcast.mp3"
                className="inline-block px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-lg font-semibold rounded-lg hover:from-green-600 hover:to-emerald-700 shadow-lg mb-4"
              >
                📥 Download Podcast
              </a>
              
              <p className="text-sm text-purple-300 mt-4">
                Share this podcast with your loved ones to preserve your legacy forever.
              </p>
            </div>
          )}

          <a
            href="/podcast"
            className="inline-block px-8 py-4 border border-purple-500/30 text-lg font-semibold rounded-lg text-purple-300 hover:text-white hover:bg-purple-900/30"
          >
            Create Another Podcast
          </a>
        </div>
      </div>
    );
  }

  if (compilingPodcast) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-gradient-to-br from-purple-900 via-black to-pink-900 min-h-screen p-8 text-center flex items-center justify-center">
          <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-12">
            <div className="inline-block animate-spin rounded-full h-20 w-20 border-b-4 border-purple-400 mb-6"></div>
            <h2 className="text-3xl font-bold text-white mb-4">🎙️ Compiling Your Podcast...</h2>
            <p className="text-lg text-purple-200 mb-4">
              Creating intro, adding AI host questions, and combining all segments
            </p>
            <p className="text-sm text-purple-400">
              This may take a minute. Please don't close this page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (processing && currentQuestion >= selectedQuestions.length - 1) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Finalizing Your Voice Clone...</h2>
          <div className="space-y-2 text-gray-600">
            <p>⏳ Stitching all audio files...</p>
            <p>⏳ Refining your voice clone with full recording...</p>
            <p>⏳ Transcribing complete podcast...</p>
            <p>⏳ Extracting personality profile...</p>
          </div>
          <p className="text-sm text-gray-500 mt-6">Creating your final, refined voice clone (2-3 minutes)</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Premium Header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center shadow-2xl">
              <MicrophoneIcon className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-white mb-3 tracking-tight">
            The Legacy Podcast
          </h1>
          <p className="text-xl text-purple-200">
            with AI Host • Preserving Your Story
          </p>
          <div className="flex gap-4 mt-4">
            <button
              onClick={() => setCustomizing(true)}
              className="px-4 py-2 text-sm text-purple-300 hover:text-white transition-colors"
            >
              ← Customize Questions
            </button>
            <button
              onClick={() => {
                if (window.confirm('Restart podcast from Question 1? This will delete your current progress.')) {
                  setCurrentQuestion(0);
                  setRecordedChunks([]);
                  setPreviewVoiceId(null);
                  setPreviewAudioUrl(null);
                }
              }}
              className="px-4 py-2 text-sm text-red-400 hover:text-red-300 transition-colors"
            >
              🔄 Restart from Question 1
            </button>
          </div>
        </div>

        {/* Main Podcast Card */}
        <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-8 md:p-12">
          
          {/* Progress Bar */}
          <div className="mb-10">
            <div className="flex justify-between text-sm text-purple-300 mb-3">
              <span className="font-medium">Question {currentQuestion + 1} of {selectedQuestions.length}</span>
              <span className="text-purple-400">{Math.round(((currentQuestion) / selectedQuestions.length) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500 shadow-lg shadow-purple-500/50"
                style={{ width: `${((currentQuestion) / selectedQuestions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* AI Host Introduction */}
          <div className="mb-8 flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center">
                <span className="text-white text-xl font-bold">AI</span>
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm text-purple-300 mb-1">Your AI Host asks:</p>
              <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-2xl p-6 shadow-xl">
                <p className="text-2xl md:text-3xl text-white font-light leading-relaxed">
                  "{selectedQuestions[currentQuestion].text}"
                </p>
              </div>
            </div>
          </div>


          {/* AI Insight */}
          {currentInsight && (
            <div className="mb-8 bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-2xl p-6">
              <p className="text-sm font-semibold text-purple-300 mb-3">💡 What I'm Learning About You:</p>
              <p className="text-lg text-white leading-relaxed">{currentInsight}</p>
            </div>
          )}
          
          {/* Preview generation status */}
          {showingPreview && (
            <div className="mb-8 bg-blue-900/30 border border-blue-500/30 rounded-2xl p-6">
              <div className="flex items-center">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400 mr-3"></div>
                <div>
                  <p className="text-sm font-semibold text-blue-300">Creating your voice preview...</p>
                  <p className="text-xs text-blue-400">Cloning voice and generating sample message</p>
                </div>
              </div>
            </div>
          )}

          {/* Recording Controls */}
          <div className="flex flex-col items-center space-y-6 my-12">
            {!isRecording && !processing ? (
              <div className="flex flex-col items-center gap-4">
                <button
                  onClick={startRecording}
                  className="group relative inline-flex items-center px-12 py-6 text-xl font-bold rounded-full shadow-2xl text-white bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 transition-all transform hover:scale-105"
                >
                  <div className="absolute inset-0 rounded-full bg-red-400 blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <MicrophoneIcon className="relative h-10 w-10 mr-4" />
                  <span className="relative">Start Recording</span>
                </button>
                
                {currentQuestion >= 1 && (
                  <button
                    onClick={async () => {
                      if (window.confirm('Finish recording now and compile podcast with current answers?')) {
                        await compileFinalPodcast(recordedChunks, audioSegments);
                      }
                    }}
                    className="px-6 py-3 text-sm font-semibold rounded-lg text-purple-300 border border-purple-500/30 hover:bg-purple-900/30 hover:text-white transition-all"
                  >
                    🎙️ Finish & Compile Podcast Now
                  </button>
                )}
              </div>
            ) : isRecording ? (
              <div className="text-center">
                <div className="text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-pink-600 mb-4 animate-pulse">
                  {formatTime(timer)}
                </div>
                <p className="text-sm text-purple-400 mb-6">
                  Max: {formatTime(selectedQuestions[currentQuestion].maxDuration)}
                </p>
                <div className="inline-flex items-center px-8 py-4 bg-red-500/20 border border-red-500/30 rounded-full mb-8 backdrop-blur-sm">
                  <div className="h-4 w-4 bg-red-500 rounded-full animate-pulse mr-4 shadow-lg shadow-red-500/50"></div>
                  <span className="text-red-300 font-semibold text-lg">Recording Live...</span>
                </div>
                <button
                  onClick={stopRecording}
                  className="px-8 py-4 border-2 border-white/20 text-lg font-semibold rounded-full text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all"
                >
                  <StopIcon className="h-6 w-6 inline mr-2" />
                  Stop & Continue
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-purple-400 mb-4"></div>
                <p className="text-purple-300 text-lg">Processing your answer...</p>
              </div>
            )}
          </div>

          {/* Completed Questions */}
          {recordedChunks.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <h3 className="text-sm font-semibold text-purple-300 mb-6 uppercase tracking-wider">
                Your Story So Far ({recordedChunks.length}/{selectedQuestions.length})
              </h3>
              <div className="space-y-4">
                {recordedChunks.map((chunk, i) => (
                  <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center flex-1">
                        <CheckCircleIcon className="h-5 w-5 text-emerald-400 mr-3 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-white font-medium mb-1">Q{i + 1}: {chunk.question}</p>
                          <p className="text-sm text-purple-300">Duration: {formatTime(chunk.duration || 0)}</p>
                          {chunk.audioPath && (
                            <p className="text-xs text-purple-400 mt-1">✓ Saved</p>
                          )}
                        </div>
                      </div>
                      <span className="text-sm text-purple-400 ml-4">{formatTime(chunk.duration)}</span>
                    </div>
                    
                    {/* Audio playback */}
                    {chunk.audio && (
                      <audio
                        controls
                        src={URL.createObjectURL(chunk.audio)}
                        className="w-full mb-4"
                      />
                    )}
                    
                    {/* AI Insight */}
                    {chunk.insight && (
                      <div className="bg-purple-900/30 border border-purple-500/20 rounded-xl p-4 mb-3">
                        <p className="text-xs font-semibold text-purple-300 mb-2">💡 What I Learned:</p>
                        <p className="text-sm text-purple-100 leading-relaxed">{chunk.insight}</p>
                      </div>
                    )}
                    
                    {/* Delete action */}
                    <div className="flex justify-end">
                      <button
                        onClick={() => {
                          const newChunks = recordedChunks.filter((_, idx) => idx !== i);
                          setRecordedChunks(newChunks);
                          if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
                        }}
                        className="text-xs text-red-400 hover:text-red-300 px-3 py-1 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors"
                      >
                        Delete Answer
                      </button>
                    </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
